import React from 'react';
import OrganisationHealthScoreBlock from '../../Dashboard/OrganisationHealthScoreBlock';
import { dashboardMuted, dashboardStatCard, dashboardStatLabel, dashboardStatValue } from '../../../theme/dashboardLayout';
import {
  isEmptyOrganisationHealthScore,
} from '../mapResponses';
import { AiForecastItemDto, AiMomentumDashboardResponse } from '../types';
import { formatShortDate } from '../utils/formatDates';
import DirectionPill from './DirectionPill';
import {
  forecastDirectionColor,
  forecastDirectionLabel,
  forecastTone,
  isForecastDisclosed,
} from './forecastHelpers';

interface Props {
  momentum: AiMomentumDashboardResponse;
  forecasts: AiForecastItemDto[];
  disclaimer: string;
  contributorCount?: number | null;
  enrolledMembers?: number | null;
  previousScore?: number | null;
}

function ForecastMomentumPanel({
  momentum,
  forecasts,
  disclaimer,
  contributorCount,
  enrolledMembers,
  previousScore,
}: Props): React.ReactElement {
  const hasDisclosedForecasts = forecasts.some(isForecastDisclosed);
  const emptyScore = isEmptyOrganisationHealthScore(momentum.population_momentum_score);
  const asOfDate = momentum.anchor_date;
  const showDirectionSummary = false;
  const quality = momentum.average_quality_coefficient;

  return (
    <div
      className="contents scroll-mt-24"
      role="region"
      aria-label="Momentum and forecasts"
      id="forecasts"
    >
      <article id="momentum" className={`${dashboardStatCard} lg:col-start-2 lg:row-start-1`}>
        <h3 className={dashboardStatLabel}>Organisation Health Score</h3>
        <div className="min-w-0 flex-1">
          <OrganisationHealthScoreBlock
            score={momentum.population_momentum_score}
            updatedAt={asOfDate}
            contributorCount={contributorCount}
            enrolledMembers={enrolledMembers}
            previousScore={previousScore}
            qualityCoefficient={quality}
          />
          {showDirectionSummary && !emptyScore ? (
            <div className="mt-4 flex flex-col items-center text-center">
              <DirectionPill direction={momentum.direction} />
              <p className={`mt-3 ${dashboardMuted}`}>{momentum.direction_label}</p>
            </div>
          ) : null}
        </div>
        {asOfDate ? (
          <p className={`mt-4 border-t border-portal-teal/10 pt-4 ${dashboardMuted}`}>
            As of {formatShortDate(asOfDate)}
          </p>
        ) : null}
      </article>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-3 lg:col-span-2 lg:col-start-1 lg:row-start-2">
      {forecasts.map((item) => {
        const directionLabel = forecastDirectionLabel(item);
        const tone = forecastTone(item);
        const directionColor = forecastDirectionColor(directionLabel);
        const disclosed = isForecastDisclosed(item);
        return (
          <article
            key={item.forecast_horizon}
            className={`${dashboardStatCard} flex min-h-[5.5rem] flex-col border-l-4 ${tone.card}`}
            aria-label={`+${item.forecast_horizon} day forecast ${directionLabel}`}
          >
            <h3 className={dashboardStatLabel}>
              +{item.forecast_horizon} day forecast
              {disclosed ? (
                <sup className="ml-0.5 font-semibold" aria-hidden="true">*</sup>
              ) : null}
            </h3>
            <p
              className={`${dashboardStatValue} ${tone.value}`}
              style={directionColor ? { color: directionColor } : undefined}
            >
              {directionLabel}
            </p>
            <p className={`mt-auto pt-3 ${dashboardMuted}`}>
              {disclosed
                ? `${item.confidence_band} confidence${item.forecast_date ? ` · ${formatShortDate(item.forecast_date)}` : ''}`
                : item.suppression_message ?? 'Not enough data to disclose this horizon.'}
            </p>
          </article>
        );
      })}
      </div>
      {hasDisclosedForecasts && disclaimer ? (
        <p className={`lg:col-span-2 lg:col-start-1 lg:row-start-3 ${dashboardMuted}`}>
          <span aria-hidden="true">* </span>
          {disclaimer}
        </p>
      ) : null}
    </div>
  );
}

export default ForecastMomentumPanel;
